import { CreateAppSchema } from "@biteasy/contracts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { AlertTriangle, ArrowUpRight } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";
import { Button } from "@/components/catalyst/Button";
import {
	Checkbox,
	CheckboxField,
	CheckboxGroup,
} from "@/components/catalyst/Checkbox";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import {
	ErrorMessage,
	Field,
	FieldGroup,
	Fieldset,
	Label,
} from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { Text } from "@/components/catalyst/Text";
import { useCanCreate } from "@/hooks/use-subscription";
import { orpc } from "@/orpc/client";

type CreateAppFormValues = z.infer<typeof CreateAppSchema>;

/**
 * New App Dialog component — rendered by the /new-app route.
 * Always shows as open; closing navigates back.
 */
export function NewAppDialog() {
	const navigate = useNavigate();
	const router = useRouter();
	const queryClient = useQueryClient();
	const {
		canCreate,
		usage,
		limit,
		isLoading: isLoadingLimits,
	} = useCanCreate("apps");

	const {
		register,
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<CreateAppFormValues>({
		resolver: zodResolver(CreateAppSchema),
		defaultValues: {
			name: "",
			platforms: ["ios", "android"],
			iosStoreLink: "",
			androidStoreLink: "",
		},
	});

	const platforms = watch("platforms") || [];
	const showIosStoreLink = platforms.includes("ios");
	const showAndroidStoreLink = platforms.includes("android");

	const createMutation = useMutation(
		orpc.apps.create.mutationOptions({
			onSuccess: (newApp) => {
				toast.success("App created successfully");
				queryClient.invalidateQueries({ queryKey: orpc.apps.list.key() });
				queryClient.invalidateQueries({
					queryKey: orpc.billing.getSubscription.key(),
				});
				reset();
				navigate({
					to: "/apps/$appId",
					params: { appId: newApp.id },
				});
			},
			onError: (error) => {
				toast.error(error.message);
			},
		}),
	);

	const onSubmit = (data: CreateAppFormValues) => {
		createMutation.mutate(data);
	};

	const handleClose = () => {
		if (!isSubmitting) {
			reset();
			router.history.back();
		}
	};

	const handlePlatformChange = (
		platform: "ios" | "android",
		checked: boolean,
	) => {
		if (checked) {
			setValue("platforms", [...platforms, platform]);
		} else {
			setValue(
				"platforms",
				platforms.filter((p) => p !== platform),
			);
			// Clear the store link when unchecking platform
			if (platform === "ios") {
				setValue("iosStoreLink", "");
			} else {
				setValue("androidStoreLink", "");
			}
		}
	};

	const handleUpgrade = () => {
		navigate({ to: "/settings", search: { tab: "billing" } });
	};

	// Show limit reached UI
	if (!isLoadingLimits && !canCreate) {
		return (
			<Dialog open onClose={handleClose}>
				<DialogTitle>App Limit Reached</DialogTitle>
				<DialogBody>
					<div className="text-center py-4">
						<div className="mx-auto w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center mb-4">
							<AlertTriangle className="size-6 text-warning" />
						</div>
						<Text className="mb-2">
							You've used{" "}
							<strong>
								{usage}/{limit}
							</strong>{" "}
							apps on your current plan.
						</Text>
						<Text className="text-base-content/60">
							Upgrade your plan to create more apps.
						</Text>
					</div>
				</DialogBody>
				<DialogActions>
					<Button plain onClick={handleClose}>
						Cancel
					</Button>
					<Button onClick={handleUpgrade}>
						<ArrowUpRight className="size-4" />
						Upgrade Plan
					</Button>
				</DialogActions>
			</Dialog>
		);
	}

	return (
		<Dialog open onClose={handleClose}>
			<DialogTitle>Create New App</DialogTitle>
			<DialogBody>
				<form
					id="create-app-form"
					onSubmit={handleSubmit(onSubmit, console.log)}
				>
					<Fieldset>
						<FieldGroup>
							<Field>
								<Label>Name</Label>
								<Input
									{...register("name")}
									placeholder="e.g. My Awesome App"
									autoFocus
								/>
								{errors.name && (
									<ErrorMessage>{errors.name.message}</ErrorMessage>
								)}
							</Field>

							<Field>
								<Label>Platforms</Label>
								<CheckboxGroup>
									<CheckboxField>
										<Checkbox
											checked={platforms.includes("ios")}
											onChange={(checked) =>
												handlePlatformChange("ios", checked)
											}
										/>
										<Label>iOS</Label>
									</CheckboxField>
									<CheckboxField>
										<Checkbox
											checked={platforms.includes("android")}
											onChange={(checked) =>
												handlePlatformChange("android", checked)
											}
										/>
										<Label>Android</Label>
									</CheckboxField>
								</CheckboxGroup>
								{errors.platforms && (
									<ErrorMessage>
										{errors.platforms.message}
										{Array.isArray(errors.platforms) &&
											"Please select at least one platform"}
									</ErrorMessage>
								)}
							</Field>

							{(showIosStoreLink || showAndroidStoreLink) && (
								<Text className="text-sm text-base-content/60">
									At least one store link is required.
								</Text>
							)}

							{showIosStoreLink && (
								<Field>
									<Label>iOS App Store Link</Label>
									<Input
										{...register("iosStoreLink")}
										placeholder="https://apps.apple.com/app/id123456789"
									/>
									{errors.iosStoreLink && (
										<ErrorMessage>{errors.iosStoreLink.message}</ErrorMessage>
									)}
								</Field>
							)}

							{showAndroidStoreLink && (
								<Field>
									<Label>Google Play Store Link</Label>
									<Input
										{...register("androidStoreLink")}
										placeholder="https://play.google.com/store/apps/details?id=com.example.app"
									/>
									{errors.androidStoreLink && (
										<ErrorMessage>
											{errors.androidStoreLink.message}
										</ErrorMessage>
									)}
								</Field>
							)}
						</FieldGroup>
					</Fieldset>
				</form>
			</DialogBody>
			<DialogActions>
				<Button plain onClick={handleClose} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button type="submit" form="create-app-form" disabled={isSubmitting}>
					{isSubmitting ? "Creating..." : "Create App"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
